export const PLANNING_SYNC_ENABLED = true;
export const PLANNING_SYNC_POLL_MS = 3000;
export const PLANNING_SYNC_EVENTS_LIMIT = 100;

const DEBUG_SHOW_DISPATCH_EDITED_LOCAL = false;
export const DEBUG_SHOW_DISPATCH_EDITED =
  process.env.NODE_ENV !== "production" && DEBUG_SHOW_DISPATCH_EDITED_LOCAL;

const BOTH_MODULES = { dispatch: true, schedule: true };
const DISPATCH_ONLY = { dispatch: true, schedule: false };
const SCHEDULE_ONLY = { dispatch: false, schedule: true };
const NO_MODULES = { dispatch: false, schedule: false };

/**
 * Operation policy for planning sync events.
 *
 * `sync` gates whether the current module processes an event; the sync-log row's
 * `resources` still decides which reload handlers run. `notify` only controls
 * user-facing toasts, so reload-only implementation details can stay quiet.
 */
export const PLANNING_OPERATION_CONFIG = {
  addWorkScopeAspect: {
    message: "added a scope",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  adjustScheduleBlockAfterDispatchDelete: {
    message: "updated schedule after dispatch scope delete",
    sync: BOTH_MODULES,
    notify: NO_MODULES,
  },
  clearScopeSkippedReason: {
    message: "cleared a skipped day reason",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  clearAssignmentSet: {
    message: "updated scope details",
    sync: DISPATCH_ONLY,
    notify: DISPATCH_ONLY,
  },
  clearWorkers: {
    message: "cleared worker assignments",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  changeDispatchScopeForDayOnly: {
    message: "changed scope for day only",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  createScheduleBlock: {
    message: "created a scope block",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  createScheduleGroup: {
    message: "created a schedule group",
    sync: SCHEDULE_ONLY,
    notify: SCHEDULE_ONLY,
  },
  deleteDayScopes: {
    message: "deleted scopes for the day",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  deleteScheduleGroup: {
    message: "deleted a schedule group",
    sync: SCHEDULE_ONLY,
    notify: SCHEDULE_ONLY,
  },
  deleteScheduleBlock: {
    message: "deleted a scope block",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  deleteWorkScopeAspect: {
    message: "deleted a scope",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  deleteWorkScopeAspects: {
    message: "deleted scopes",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  duplicateScopes: {
    message: "copied scopes",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  markDayDispatched: {
    message: "marked the day dispatched",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  markDayReviewed: {
    message: "marked the day reviewed",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  mergeAdjacentScheduleBlocks: {
    message: "merged adjacent scope blocks",
    sync: BOTH_MODULES,
    notify: NO_MODULES,
  },
  moveScheduleBlock: {
    message: "moved a scope block",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  pasteScheduleBlock: {
    message: "pasted a scope block",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  patchDayDetails: {
    message: "updated day details",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  patchScheduleGroup: {
    message: "renamed a schedule group",
    sync: SCHEDULE_ONLY,
    notify: SCHEDULE_ONLY,
  },
  patchScopeAspects: {
    message: "updated worker assignments",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  reconcileOrphanScheduleBlock: {
    message: "updated schedule from dispatch",
    sync: BOTH_MODULES,
    notify: NO_MODULES,
  },
  removeScheduleDayCrewOverride: {
    message: "removed a crew override",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  removeScopeSkipped: {
    message: "unskipped a day",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  removeScopeSkippedUnavailableWorkers: {
    message: "removed unavailable workers",
    sync: BOTH_MODULES,
    notify: NO_MODULES,
  },
  setScopeSkipped: {
    message: "skipped a day",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  splitScheduleBlockAfterDispatchDelete: {
    message: "split schedule after dispatch scope delete",
    sync: BOTH_MODULES,
    notify: NO_MODULES,
  },
  updateDayOffWork: {
    message: "updated off-work workers",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updateDispatchStage: {
    message: "updated dispatch status",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updatePlanningDayLock: {
    message: "updated day lock",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  // Needs to be included in Schedule for block scope updates
  updateDispatchScope: {
    message: "updated a scope",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updatePastedScheduleCrews: {
    message: "updated pasted scope crews",
    sync: BOTH_MODULES,
    notify: NO_MODULES,
  },
  updatePastedScopeSkipped: {
    message: "updated pasted skipped days",
    sync: BOTH_MODULES,
    notify: NO_MODULES,
  },
  updateScheduleBlock: {
    message: "updated a scope block",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updateScheduleBlockDefaultCrew: {
    message: "updated default crew",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updateScheduleConflictCrewOverrides: {
    message: "updated conflict crew overrides",
    sync: BOTH_MODULES,
    notify: SCHEDULE_ONLY,
  },
  updateScheduleDayCrew: {
    message: "updated daily crew",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updateScheduleOverrideRange: {
    message: "updated daily crew override range",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updateScope: {
    message: "updated a scope",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updateScopeDetails: {
    message: "updated scope details",
    sync: DISPATCH_ONLY,
    notify: DISPATCH_ONLY,
  },
  updateScopeExtraNotes: {
    message: "updated scope notes",
    sync: DISPATCH_ONLY,
    notify: DISPATCH_ONLY,
  },
  updateScopeNotes: {
    message: "updated scope notes",
    sync: DISPATCH_ONLY,
    notify: DISPATCH_ONLY,
  },
  updateScopeSkippedReason: {
    message: "updated a skipped day reason",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updateScopeWorkers: {
    message: "updated worker assignments",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
  updateWorkScopeAspect: {
    message: "updated a scope",
    sync: BOTH_MODULES,
    notify: BOTH_MODULES,
  },
};

export const PLANNING_OPERATION_MESSAGES = {
  ...Object.fromEntries(
    Object.entries(PLANNING_OPERATION_CONFIG).map(([key, config]) => [
      key,
      config.message,
    ])
  ),
  scheduleCannotChangeScopeDatesDueToLockedDays:
    "Cannot update scope dates as they would span locked days",
  scheduleCannotMoveScopeIntoLockedDays: "Cannot move scope into locked days",
  scheduleUnableToSetSkippedOnLockedDays: "Unable to set skipped on locked days",
  scheduleCannotPasteScopeSpanningLockedDays:
    "Cannot paste scope here as it would span locked days",
  scheduleCannotChangeCrewOverrideDueToLockedDays:
    "Cannot change crew override due to locked days",
};

/** Tooltip and hover-help copy for planning UI surfaces. */
export const PLANNING_TOOLTIP_MESSAGES = {
  lockedCrewAction: "Cannot edit crew due to locked day",
  copyExistingCrew: "Copy Existing Crew",
  pasteCrew: "Paste Crew",
  pasteCrewDisabled: "Matches copied crew",
  scopeSettingsLocked: "Cannot change blocks with dispatch reviewed days (locked)",
  scopeSettings: "Scope settings",
  editCrewForDay: "Edit crew for this day",
  revertCrewForDay: "Revert to default crew for this day",
  editToAddCrew: "Edit to add crew",
  clickToEditGroupName: "Click to edit group name",
  deleteGroup: "Delete group",
  showEmptyProjects:
    "Show jobs with no schedule rows/bars on visible days, including empty Dispatch groups auto-created from Dispatch",
  showEmptyProjectsDispatch:
    "Show jobs with no work scopes on the visible days of this week",
  dayLockedDueToDispatchReview: "Day locked due to Dispatch review",
  clickToAddReason: "Click to add Reason",
  editingDisabledWhenLocked: "Editing is disabled when locked.",
  draggingDisabledDueToLockedDay: "Dragging disabled due to locked day",
  scopeBarDragDisabledInstructions:
    "(Dragging disabled due to locked days in scope). ⌘ or Ctrl+click to skip day.",
  scopeBarDragInstructions: "Drag to reposition scope, ⌘ or Ctrl+click to skip day.",
  collapseAllProjectsAndGroups: "Collapse all projects and groups",
  expandAllProjectsAndGroups: "Expand all projects and groups",
};

/** Return the operation policy for a planning sync event. */
export function getPlanningOperationConfig(operation) {
  return PLANNING_OPERATION_CONFIG[operation] ?? null;
}

/** Whether a sync event should refresh the given planning module. */
export function shouldSyncPlanningOperation(operation, module) {
  const config = getPlanningOperationConfig(operation);
  if (!config) {
    return true;
  }
  return config.sync?.[module] !== false;
}

/** Whether a sync event should show a toast in the given planning module. */
export function shouldNotifyPlanningOperation(operation, module) {
  const config = getPlanningOperationConfig(operation);
  if (!config) {
    return true;
  }
  return config.notify?.[module] !== false;
}

/** Return a plain local/sync message for a planning operation. */
export function getPlanningOperationMessage(operation, module = "planning") {
  return (
    getPlanningOperationConfig(operation)?.message ??
    `updated ${String(module).toLowerCase()} data`
  );
}
